import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Layers,
  Package,
  Plus,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import DataActAssessmentPanel from "../components/DataActAssessmentPanel";
import FieldLabel from "../components/FieldLabel";
import ProductDependencyTrackPanel from "../components/ProductDependencyTrackPanel";
import ProductSecurityOverview from "../components/ProductSecurityOverview";
import ProductThreatModelPanel from "../components/ProductThreatModelPanel";
import RelationshipPropertyRow, {
  RelationshipRowEntry,
} from "../components/RelationshipPropertyRow";
import SDLAssessmentPanel from "../components/SDLAssessmentPanel";
import TagField from "../components/TagField";
import TypeaheadUserInput from "../components/TypeaheadUserInput";
import { CRA_CLASSIFICATION_OPTIONS } from "../constants/craClassification";
import { productFieldDescriptions } from "../constants/productFieldDescriptions";
import { SHOW_DATAACT_FEATURES } from "../featureFlags";
import { useAuth } from "../hooks/useAuth";
import { DT_VERSIONS_PARAM } from "../hooks/useDtVersionSelection";
import { apiService } from "../services/api";
import {
  Application,
  canManageAssessment,
  isAdmin,
  ProductTagInput,
  RelationshipInput,
  RelationshipTypeDefinition,
  TagDefinitionCategory,
  User as UserType,
} from "../types";

export default function ProductDetailPage() {
  // Feature flags
  const SHOW_SDL_FEATURES = true; // Set to false to hide SDL assessments
  const SHOW_REQUEST_OWNERSHIP = false; // Set to false to hide Request Ownership button

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Where the back control returns to.
  //
  // The Products page records the list URL it was showing -- filters, page and
  // all -- in history state when opening a product, so going back restores
  // that view instead of the unfiltered list (issue #26). Reached any other
  // way, such as a bookmark or a link from the dashboard, there is nothing to
  // restore and the plain list is the right destination.
  const backTo =
    typeof (location.state as { from?: unknown } | null)?.from === "string"
      ? (location.state as { from: string }).from
      : "/products";
  const [isEditing, setIsEditing] = useState(false);
  const [showLinkApplications, setShowLinkApplications] = useState(false);
  const [showBasicInfo, setShowBasicInfo] = useState(true);
  const [activeTab, setActiveTab] = useState<"overview" | "security">(() =>
    searchParams.has(DT_VERSIONS_PARAM) ? "security" : "overview",
  );
  const [applicationSearch, setApplicationSearch] = useState("");
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    businessArea: "",
    division: "",
    category: "",
    ownerId: "",
    apmNumber: "",
    productResponsibleId: "",
    businessAccountableId: "",
    sdlResponsibleId: "",
    developmentResponsibility: "",
    customization: "",
    lifecycleStatus: "",
    craClassification: "",
    keyProduct: false,
  });
  const [selectedTags, setSelectedTags] = useState<ProductTagInput[]>([]);
  // Outgoing relationships being edited. Saved together with the rest of the
  // product form via the main Save Changes button, identical to how tags work.
  // _targetName is carried locally for rendering and stripped before sending.
  const [selectedRelationships, setSelectedRelationships] = useState<
    (RelationshipInput & { _targetName?: string })[]
  >([]);

  // Fetch tag definitions
  const { data: tagDefinitions } = useQuery(
    "tagDefinitions",
    () => apiService.getTagDefinitions(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as TagDefinitionCategory[])
          : [],
    },
  );

  // Fetch relationship type definitions
  const { data: relationshipTypes } = useQuery(
    "relationshipTypes",
    () => apiService.getRelationshipTypes(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as RelationshipTypeDefinition[])
          : [],
    },
  );

  // Fetch product details
  const {
    data: productData,
    isLoading,
    error,
  } = useQuery(["product", id], () => apiService.getProduct(id!), {
    enabled: !!id,
  });

  // Fetch assessment status for admin users
  const { data: assessmentStatusData } = useQuery(
    ["assessmentStatus", id],
    () => apiService.getAssessmentStatus(id!),
    {
      enabled: !!id,
      retry: 1,
    },
  );

  // Fetch SDL assessment periods for this product
  const { data: periodsData, isLoading: periodsLoading } = useQuery(
    ["sdlAssessmentPeriods", id],
    () => apiService.getSDLAssessmentPeriodsForProduct(id!),
    { enabled: !!id },
  );

  // Fetch business areas for editing
  const { data: businessAreasResponse, isLoading: loadingBusinessAreas } =
    useQuery("businessAreas", () => apiService.getBusinessAreas(), {
      enabled: isEditing,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    });

  // Fetch divisions when business area is selected during editing
  const { data: divisionsResponse, isLoading: loadingDivisions } = useQuery(
    ["divisions", editForm.businessArea],
    () => apiService.getDivisions(editForm.businessArea),
    {
      enabled: isEditing && !!editForm.businessArea,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  // Fetch users from the product's business area for owner dropdown
  const { data: usersInBAResponse } = useQuery(
    ["users-by-ba", editForm.businessArea],
    () => apiService.getUsers(),
    {
      enabled: isEditing && !!editForm.businessArea,
      select: (response) => {
        const users = (response.data as any)?.data || [];
        // Filter users by the product's business area
        return users.filter(
          (u: any) => u.businessArea === editForm.businessArea,
        );
      },
    },
  );

  // Fetch all users for the responsibility dropdowns
  const { data: usersResponse, isLoading: loadingUsers } = useQuery(
    "users",
    () => apiService.getUsers(),
    {
      enabled: isEditing,
      select: (response) =>
        response.data.success ? (response.data.data as UserType[]) : [],
    },
  );

  // Applications search query (for linking)
  const { data: appsData } = useQuery(
    ["apps-for-link", applicationSearch],
    () => apiService.getApplications({ search: applicationSearch, limit: 50 }),
    { enabled: showLinkApplications },
  );
  const allApplications: Application[] =
    (appsData?.data as any)?.data?.applications || [];

  // Link/unlink application mutations
  const linkApplicationsMutation = useMutation(
    (applicationIds: string[]) =>
      apiService.linkProductApplications(id!, applicationIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        setShowLinkApplications(false);
        setApplicationSearch("");
        toast.success("Application linked successfully");
      },
      onError: () => {
        toast.error("Failed to link application");
      },
    },
  );

  const unlinkApplicationMutation = useMutation(
    (applicationId: string) =>
      apiService.unlinkProductApplication(id!, applicationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        toast.success("Application unlinked");
      },
      onError: () => {
        toast.error("Failed to unlink application");
      },
    },
  );

  // Delete product mutation
  const deleteMutation = useMutation(
    (productId: string) => apiService.deleteProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product deleted successfully");
        navigate("/products");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to delete product");
      },
    },
  );

  // Approve product mutation
  const approveMutation = useMutation(
    (productId: string) => apiService.approveProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["products"]);
        toast.success("Product approved successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to approve product");
      },
    },
  );

  // Retire product mutation
  const retireMutation = useMutation(
    (productId: string) => apiService.retireProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["products"]);
        toast.success("Product retired successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to retire product");
      },
    },
  );

  // Un-retire product mutation (admin recovery path for a mistaken retirement)
  const unretireMutation = useMutation(
    (payload: { productId: string; lifecycleStatus: string }) =>
      apiService.unretireProduct(payload.productId, payload.lifecycleStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["products"]);
        toast.success("Product un-retired successfully");
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to un-retire product",
        );
      },
    },
  );

  // Permanent delete product mutation
  const permanentDeleteMutation = useMutation(
    (productId: string) => apiService.permanentDeleteProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product permanently deleted");
        navigate("/products");
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to permanently delete product",
        );
      },
    },
  );

  // Update product mutation
  const updateMutation = useMutation(
    (data: any) => apiService.updateProduct(id!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["products"]);
        toast.success("Product updated successfully");
        setIsEditing(false);
        const resumeAfter = searchParams.get("resumeAfter");
        if (resumeAfter === "data-act") {
          navigate(`/products/${id}/data-act-assessment?resume=1`);
        } else if (searchParams.toString()) {
          setSearchParams({}, { replace: true });
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update product");
      },
    },
  );

  // Reset division when business area changes during editing
  useEffect(() => {
    if (isEditing && editForm.businessArea && editForm.division) {
      const productResponse = (productData?.data as any)?.data;
      // Only reset if the business area has actually changed from the original
      if (
        productResponse &&
        editForm.businessArea !== productResponse.businessArea
      ) {
        setEditForm((prev) => ({ ...prev, division: "" }));
      }
    }
  }, [editForm.businessArea, isEditing, productData]);

  // Deep-link support: enter edit mode automatically when `?edit=true` is set
  // (e.g. from the Data Act prerequisite gate).
  const wantsEdit = searchParams.get("edit") === "true";
  const focusTag = searchParams.get("focus");
  useEffect(() => {
    if (wantsEdit && !isEditing && productData) {
      handleEdit();
    }
    // Intentional: only reacts to wantsEdit/productData becoming truthy; we
    // don't want to re-open edit mode after the user cancels.
  }, [wantsEdit, productData]);

  // Scroll to and highlight the requested tag field once edit mode is active.
  // The target element may not be in the DOM yet (e.g. relationships render
  // after relationshipTypes load), so we retry briefly until it appears.
  useEffect(() => {
    if (!isEditing || !focusTag) return;
    let attempts = 0;
    let retryTimer: number | undefined;
    let clearTimer: number | undefined;
    let highlightedEl: HTMLElement | null = null;
    const ringClasses = [
      "ring-2",
      "ring-primary-500",
      "ring-offset-2",
      "dark:ring-offset-gray-900",
    ];
    const tryFocus = () => {
      const el = document.getElementById(`tag-${focusTag}`);
      if (!el) {
        if (attempts++ < 30) {
          retryTimer = window.setTimeout(tryFocus, 100);
        }
        return;
      }
      const container = el.closest("div");
      (container ?? el).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      el.classList.add(...ringClasses);
      highlightedEl = el;
      clearTimer = window.setTimeout(() => {
        el.classList.remove(...ringClasses);
      }, 3000);
    };
    tryFocus();
    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
      if (clearTimer) window.clearTimeout(clearTimer);
      if (highlightedEl) highlightedEl.classList.remove(...ringClasses);
    };
  }, [isEditing, focusTag]);

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      deleteMutation.mutate(id!);
    }
  };

  const handleEdit = () => {
    const productResponse = (productData?.data as any)?.data;
    if (productResponse) {
      setEditForm({
        name: productResponse.name,
        description: productResponse.description || "",
        businessArea: productResponse.businessArea,
        division: productResponse.division,
        category: productResponse.category || "",
        ownerId: productResponse.ownerId || "",
        apmNumber: productResponse.apmNumber || "",
        productResponsibleId: productResponse.productResponsibleId || "",
        businessAccountableId: productResponse.businessAccountableId || "",
        sdlResponsibleId: productResponse.sdlResponsibleId || "",
        developmentResponsibility:
          productResponse.developmentResponsibility || "",
        customization: productResponse.customization || "",
        lifecycleStatus: productResponse.lifecycleStatus || "",
        craClassification: productResponse.craClassification || "",
        keyProduct: productResponse.keyProduct || false,
      });
      if (productResponse.productTags) {
        setSelectedTags(
          productResponse.productTags.map((t: any) => ({
            key: t.key,
            value: t.value,
          })),
        );
      } else {
        setSelectedTags([]);
      }
      setSelectedRelationships(
        outgoingRelsToInputs(productResponse.productRelationships || []),
      );
      setIsEditing(true);
    }
  };

  const handleTagChange = (keyId: string, values: string[]) => {
    setSelectedTags((prev) => [
      ...prev.filter((t) => t.key !== keyId),
      ...values.map((value) => ({ key: keyId, value })),
    ]);
  };

  const handleSave = () => {
    // Validate required fields
    if (!editForm.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!editForm.businessArea.trim()) {
      toast.error("Business area is required");
      return;
    }
    if (!editForm.division.trim()) {
      toast.error("Division/Business Unit is required");
      return;
    }

    updateMutation.mutate({
      ...editForm,
      tags: selectedTags,
      relationships: selectedRelationships.map(({ _targetName, ...r }) => r),
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedRelationships([]);
    setEditForm({
      name: "",
      description: "",
      businessArea: "",
      division: "",
      category: "",
      ownerId: "",
      apmNumber: "",
      productResponsibleId: "",
      businessAccountableId: "",
      sdlResponsibleId: "",
      developmentResponsibility: "",
      customization: "",
      lifecycleStatus: "",
      craClassification: "",
      keyProduct: false,
    });
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
  };

  // Special handlers for responsibility fields
  const handleProductResponsibleChange = (value: string) => {
    setEditForm((prev) => ({
      ...prev,
      productResponsibleId: value,
    }));
  };

  const handleBusinessAccountableChange = (value: string) => {
    setEditForm((prev) => ({
      ...prev,
      businessAccountableId: value,
    }));
  };

  const handleSdlResponsibleChange = (value: string) => {
    setEditForm((prev) => ({
      ...prev,
      sdlResponsibleId: value,
    }));
  };

  const handleApprove = () => {
    if (window.confirm("Are you sure you want to approve this product?")) {
      approveMutation.mutate(id!);
    }
  };

  const handleRetire = () => {
    if (
      window.confirm(
        "Are you sure you want to retire this product? This will make it read-only.",
      )
    ) {
      retireMutation.mutate(id!);
    }
  };

  const handleUnretire = () => {
    // The pre-retirement status is not stored, so let the admin choose where
    // the product lands. Empty input falls back to the backend default.
    const target = window.prompt(
      "Un-retire this product and restore it to which lifecycle status?\n\n" +
        "Planned / In Development / Released / Obsolete",
      "Released",
    );
    if (target === null) return;
    unretireMutation.mutate({
      productId: id!,
      lifecycleStatus: target.trim(),
    });
  };

  const handlePermanentDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to PERMANENTLY delete this product? This action cannot be undone. All assessments, applications, and history will be lost.",
      )
    ) {
      permanentDeleteMutation.mutate(id!);
    }
  };

  const outgoingRelsToInputs = (
    rels: any[],
  ): (RelationshipInput & { _targetName?: string })[] =>
    rels
      .filter((r: any) => r.sourceId === id)
      .map((r: any) => ({
        targetProductId: r.targetId,
        type: r.type,
        criticality: r.criticality || undefined,
        since: r.since ? r.since.substring(0, 10) : undefined,
        until: r.until ? r.until.substring(0, 10) : undefined,
        notes: r.notes || undefined,
        _targetName: r.targetProduct?.name || "",
      }));

  // SDL Assessment data
  const periods = (periodsData?.data as any)?.data || [];

  // Populate edit form when product data loads - must be before any conditional returns
  useEffect(() => {
    const productResponse = (productData?.data as any)?.data;
    if (productResponse) {
      setEditForm({
        name: productResponse.name,
        description: productResponse.description || "",
        businessArea: productResponse.businessArea,
        division: productResponse.division,
        category: productResponse.category || "",
        ownerId: productResponse.ownerId || "",
        apmNumber: productResponse.apmNumber || "",
        productResponsibleId: productResponse.productResponsibleId || "",
        businessAccountableId: productResponse.businessAccountableId || "",
        sdlResponsibleId: productResponse.sdlResponsibleId || "",
        developmentResponsibility:
          productResponse.developmentResponsibility || "",
        customization: productResponse.customization || "",
        lifecycleStatus: productResponse.lifecycleStatus || "",
        craClassification: productResponse.craClassification || "",
        keyProduct: productResponse.keyProduct || false,
      });
    }
  }, [productData]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  // Unwrap ApiResponse shape: AxiosResponse<{ success, data: Product }>
  const productResponse = (productData?.data as any)?.data;

  if (error || !productResponse) {
    return (
      <div className="text-center py-8">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Product not found
        </h3>
        <p className="text-gray-600 mb-4">
          The product you're looking for doesn't exist or has been removed.
        </p>
        <Link to={backTo} className="btn btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  const product = productResponse;

  const linkedApplicationIds = new Set(
    product?.applications?.map((a: Application) => a.id) || [],
  );
  const availableApplications = allApplications.filter(
    (a) => !linkedApplicationIds.has(a.id),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(backTo)}
            className="text-gray-600 hover:text-gray-900"
            title="Back to products"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            {isEditing ? (
              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, name: e.target.value }))
                }
                className="text-2xl font-bold text-gray-900 dark:text-gray-100 bg-transparent dark:bg-transparent border-b-2 border-primary-500 focus:outline-none focus:border-primary-700"
                placeholder="Product name"
              />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {product.name}
              </h1>
            )}
            <p className="text-gray-600">Product Details</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={updateMutation.isLoading}
                className="btn btn-primary"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateMutation.isLoading ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                disabled={updateMutation.isLoading}
                className="btn btn-outline"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
            </>
          ) : (
            <>
              {(isAdmin(user) || product.ownerId === user?.id) &&
                product.lifecycleStatus !== "Retired" && (
                  <button onClick={handleEdit} className="btn btn-outline">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
              {(isAdmin(user) || product.ownerId === user?.id) &&
                product.lifecycleStatus !== "Retired" && (
                  <button
                    onClick={handleRetire}
                    disabled={retireMutation.isLoading}
                    className="btn btn-outline text-amber-600 border-amber-600 hover:bg-amber-50"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    {retireMutation.isLoading ? "Retiring..." : "Retire"}
                  </button>
                )}
              {isAdmin(user) &&
                (product.lifecycleStatus === "Retired" ||
                  product.lifecycleStatus === "Deleted") && (
                  <button
                    onClick={handleUnretire}
                    disabled={unretireMutation.isLoading}
                    className="btn btn-outline text-green-600 border-green-600 hover:bg-green-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {unretireMutation.isLoading
                      ? "Un-retiring..."
                      : "Un-retire"}
                  </button>
                )}
              {isAdmin(user) &&
                (product.lifecycleStatus === "Retired" ||
                  product.lifecycleStatus === "Deleted") && (
                  <button
                    onClick={handlePermanentDelete}
                    disabled={permanentDeleteMutation.isLoading}
                    className="btn btn-outline text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {permanentDeleteMutation.isLoading
                      ? "Deleting..."
                      : "Permanently Delete"}
                  </button>
                )}
              {isAdmin(user) &&
                product.lifecycleStatus !== "Retired" &&
                product.lifecycleStatus !== "Deleted" && (
                  <button
                    onClick={handleDelete}
                    className="btn btn-outline text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                )}
            </>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Product views">
          {(
            [
              { id: "overview", label: "Overview", icon: Package },
              { id: "security", label: "Security", icon: ShieldCheck },
            ] as const
          ).map((tab) => {
            const Icon = tab.icon;
            // Editing lives on the Overview tab, so keep it selected while editing.
            const isActive = (isEditing ? "overview" : activeTab) === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                disabled={isEditing && tab.id !== "overview"}
                aria-current={isActive ? "page" : undefined}
                className={`inline-flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                  isActive
                    ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "security" && !isEditing && (
        <div className="space-y-6">
          <ProductSecurityOverview productId={id!} />

          {/* Dependency-Track aggregated CVE findings across all applications */}
          <ProductDependencyTrackPanel productId={id!} />

          {/* Threat models linked to this product */}
          <ProductThreatModelPanel productId={id!} />
        </div>
      )}

      {(activeTab === "overview" || isEditing) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Product Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <button
                type="button"
                onClick={() => setShowBasicInfo((v) => !v)}
                disabled={isEditing}
                className={`flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 ${showBasicInfo || isEditing ? "mb-4" : ""}`}
              >
                {showBasicInfo || isEditing ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Basic Information
              </button>
              {(showBasicInfo || isEditing) && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel
                        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                        description={productFieldDescriptions.businessArea}
                      >
                        Business Area
                      </FieldLabel>
                      {isEditing ? (
                        <select
                          value={editForm.businessArea}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              businessArea: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          disabled={loadingBusinessAreas}
                        >
                          <option value="">Select a business area</option>
                          {businessAreasResponse?.map((area) => (
                            <option key={area} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                          {product.businessArea}
                        </p>
                      )}
                    </div>
                    <div>
                      <FieldLabel
                        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                        description={productFieldDescriptions.division}
                      >
                        Division/Business Unit
                      </FieldLabel>
                      {isEditing ? (
                        <select
                          value={editForm.division}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              division: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          disabled={!editForm.businessArea || loadingDivisions}
                        >
                          <option value="">
                            {!editForm.businessArea
                              ? "Select a business area first"
                              : loadingDivisions
                                ? "Loading divisions/business units..."
                                : "Select a division/business unit"}
                          </option>
                          {divisionsResponse?.map((division) => (
                            <option key={division} value={division}>
                              {division}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                          {product.division}
                        </p>
                      )}
                    </div>
                    <div>
                      <FieldLabel
                        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                        description={productFieldDescriptions.category}
                      >
                        Category
                      </FieldLabel>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.category}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              category: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          placeholder="Category (optional)"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                          {product.category || "Not specified"}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                        Owner
                      </label>
                      {isEditing ? (
                        <select
                          value={editForm.ownerId}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              ownerId: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">Select Owner</option>
                          {usersInBAResponse?.map((user: any) => (
                            <option key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex items-center mt-1">
                          <User className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {product.owner.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-4">
                    <FieldLabel
                      className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                      description={productFieldDescriptions.description}
                    >
                      Description
                    </FieldLabel>
                    {isEditing ? (
                      <textarea
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Product description (optional)"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                        {product.description || "No description provided"}
                      </p>
                    )}
                  </div>

                  {/* Product Details Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Product Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* APM Number */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={productFieldDescriptions.apmNumber}
                        >
                          APM Number
                        </FieldLabel>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.apmNumber}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                apmNumber: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter APM number"
                          />
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.apmNumber || "Not specified"}
                          </p>
                        )}
                      </div>

                      {/* Development Responsibility */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={
                            productFieldDescriptions.developmentResponsibility
                          }
                        >
                          Development Responsibility
                        </FieldLabel>
                        {isEditing ? (
                          <select
                            value={editForm.developmentResponsibility}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                developmentResponsibility: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            <option value="">
                              Select development responsibility
                            </option>
                            <option value="Sandvik">Sandvik</option>
                            <option value="Supplier">Supplier</option>
                            <option value="Sub-Contractor">
                              Sub-Contractor
                            </option>
                          </select>
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.developmentResponsibility ||
                              "Not specified"}
                          </p>
                        )}
                      </div>

                      {/* Customization */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={productFieldDescriptions.customization}
                        >
                          Customization
                        </FieldLabel>
                        {isEditing ? (
                          <select
                            value={editForm.customization}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                customization: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            <option value="">Select customization type</option>
                            <option value="Customized">Customized</option>
                            <option value="COTS">COTS</option>
                          </select>
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.customization || "Not specified"}
                          </p>
                        )}
                      </div>

                      {/* Lifecycle Status */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={productFieldDescriptions.lifecycleStatus}
                        >
                          Lifecycle Status
                        </FieldLabel>
                        {isEditing ? (
                          <select
                            value={editForm.lifecycleStatus}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                lifecycleStatus: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            <option value="">Select lifecycle status</option>
                            <option value="Planned">Planned</option>
                            <option value="In Development">
                              In Development
                            </option>
                            <option value="Released">Released</option>
                            <option value="Retired">Retired</option>
                            <option value="Obsolete">Obsolete</option>
                          </select>
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.lifecycleStatus || "Not specified"}
                          </p>
                        )}
                      </div>

                      {tagDefinitions
                        ?.flatMap((category) => category.keys)
                        .map((tagKey) => {
                          const source: { key: string; value: string }[] =
                            isEditing
                              ? selectedTags
                              : product.productTags || [];
                          const selected = source
                            .filter((t) => t.key === tagKey.id)
                            .map((t) => t.value);
                          const labels = selected
                            .map(
                              (v: string) =>
                                tagKey.values.find((tv) => tv.value === v)
                                  ?.label,
                            )
                            .filter(Boolean)
                            .join(", ");
                          return (
                            <div key={tagKey.id}>
                              <FieldLabel
                                htmlFor={`tag-${tagKey.id}`}
                                className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                                description={tagKey.description}
                              >
                                {tagKey.label}
                              </FieldLabel>
                              {isEditing ? (
                                <TagField
                                  id={`tag-${tagKey.id}`}
                                  tagKey={tagKey}
                                  selectedValues={selected}
                                  onChange={(values) =>
                                    handleTagChange(tagKey.id, values)
                                  }
                                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                />
                              ) : (
                                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                                  {labels || "Not specified"}
                                </p>
                              )}
                            </div>
                          );
                        })}

                      {/* Key Product */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={productFieldDescriptions.keyProduct}
                        >
                          Key Product
                        </FieldLabel>
                        {isEditing ? (
                          <div className="mt-1 flex items-center">
                            <input
                              type="checkbox"
                              checked={editForm.keyProduct || false}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  keyProduct: e.target.checked,
                                }))
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                            />
                            <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                              Mark as key product
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center mt-1">
                            {product.keyProduct ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Key Product
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Standard Product
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Relationships Section */}
                  {relationshipTypes &&
                    relationshipTypes.length > 0 &&
                    ((product.productRelationships &&
                      product.productRelationships.length > 0) ||
                      (isEditing &&
                        (isAdmin(user) || product.ownerId === user?.id))) && (
                      <div
                        id="tag-relationships"
                        className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 rounded-md"
                      >
                        <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
                          Links
                        </h3>
                        <div className="grid grid-cols-1 gap-4">
                          {relationshipTypes.flatMap((typeDef) => {
                            const canEdit =
                              isAdmin(user) || product.ownerId === user?.id;

                            // Outgoing entries: from edit state when editing, from
                            // persisted data otherwise. Names are carried through so
                            // the row can render chips without extra lookups.
                            const outgoingEntries: RelationshipRowEntry[] =
                              isEditing
                                ? selectedRelationships
                                    .filter((r) => r.type === typeDef.id)
                                    .map((r, i) => ({
                                      key: `out:${r.targetProductId}:${i}`,
                                      otherProductId: r.targetProductId,
                                      otherProductName: r._targetName || "",
                                      isOutgoing: true,
                                      criticality: r.criticality,
                                      since: r.since,
                                      until: r.until,
                                      notes: r.notes,
                                    }))
                                : (product.productRelationships || [])
                                    .filter(
                                      (r: any) =>
                                        r.sourceId === id &&
                                        r.type === typeDef.id,
                                    )
                                    .map((r: any) => ({
                                      key: r.id,
                                      otherProductId: r.targetProduct.id,
                                      otherProductName: r.targetProduct.name,
                                      isOutgoing: true,
                                      criticality: r.criticality || undefined,
                                      since: r.since
                                        ? r.since.substring(0, 10)
                                        : undefined,
                                      until: r.until
                                        ? r.until.substring(0, 10)
                                        : undefined,
                                      notes: r.notes || undefined,
                                    }));

                            // Incoming entries are always read-only in this view;
                            // they're managed from the other product's page.
                            const incomingEntries: RelationshipRowEntry[] = (
                              product.productRelationships || []
                            )
                              .filter(
                                (r: any) =>
                                  r.targetId === id && r.type === typeDef.id,
                              )
                              .map((r: any) => ({
                                key: r.id,
                                otherProductId: r.sourceProduct.id,
                                otherProductName: r.sourceProduct.name,
                                isOutgoing: false,
                                managedOnProductId: r.sourceProduct.id,
                                managedOnProductName: r.sourceProduct.name,
                                criticality: r.criticality || undefined,
                                since: r.since
                                  ? r.since.substring(0, 10)
                                  : undefined,
                                until: r.until
                                  ? r.until.substring(0, 10)
                                  : undefined,
                                notes: r.notes || undefined,
                              }));

                            const handleOutgoingChange = (
                              next: RelationshipRowEntry[],
                            ) => {
                              const nextForType = next.map((e) => ({
                                targetProductId: e.otherProductId,
                                type: typeDef.id,
                                criticality: e.criticality,
                                since: e.since,
                                until: e.until,
                                notes: e.notes,
                                _targetName: e.otherProductName,
                              }));
                              setSelectedRelationships((prev) => [
                                ...prev.filter((r) => r.type !== typeDef.id),
                                ...nextForType,
                              ]);
                            };

                            // Asymmetric type: render two separate rows — one for
                            // the outgoing perspective (typeDef.label) and one for
                            // the inverse (typeDef.inverseLabel, read-only).
                            if (typeDef.inverseLabel) {
                              const rows: JSX.Element[] = [];
                              if (
                                outgoingEntries.length > 0 ||
                                (isEditing && canEdit)
                              ) {
                                rows.push(
                                  <RelationshipPropertyRow
                                    key={`${typeDef.id}:out`}
                                    typeDef={typeDef}
                                    entries={outgoingEntries}
                                    currentProductId={id!}
                                    isEditing={isEditing}
                                    canEdit={canEdit}
                                    hideDirectionGlyph
                                    onOutgoingChange={handleOutgoingChange}
                                  />,
                                );
                              }
                              if (incomingEntries.length > 0) {
                                rows.push(
                                  <RelationshipPropertyRow
                                    key={`${typeDef.id}:in`}
                                    typeDef={typeDef}
                                    labelOverride={typeDef.inverseLabel}
                                    entries={incomingEntries}
                                    currentProductId={id!}
                                    isEditing={false}
                                    canEdit={false}
                                    hideDirectionGlyph
                                    onOutgoingChange={() => {}}
                                  />,
                                );
                              }
                              return rows;
                            }

                            // Symmetric type: single combined row with ← glyph on
                            // incoming chips.
                            const entries = [
                              ...outgoingEntries,
                              ...incomingEntries,
                            ];
                            return [
                              <RelationshipPropertyRow
                                key={typeDef.id}
                                typeDef={typeDef}
                                entries={entries}
                                currentProductId={id!}
                                isEditing={isEditing}
                                canEdit={canEdit}
                                onOutgoingChange={handleOutgoingChange}
                              />,
                            ];
                          })}
                        </div>
                      </div>
                    )}

                  {/* Responsibilities Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Responsibilities
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Product owner */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={
                            productFieldDescriptions.productResponsibleId
                          }
                        >
                          Product owner
                        </FieldLabel>
                        {isEditing ? (
                          <TypeaheadUserInput
                            id="productResponsibleId"
                            name="productResponsibleId"
                            value={editForm.productResponsibleId || ""}
                            onChange={handleProductResponsibleChange}
                            users={usersResponse || []}
                            loading={loadingUsers}
                            placeholder="Enter email or select user"
                            className="mt-1"
                          />
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.productResponsible?.name || "Not assigned"}
                          </p>
                        )}
                      </div>

                      {/* Business Accountable */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={
                            productFieldDescriptions.businessAccountableId
                          }
                        >
                          Business Accountable
                        </FieldLabel>
                        {isEditing ? (
                          <TypeaheadUserInput
                            id="businessAccountableId"
                            name="businessAccountableId"
                            value={editForm.businessAccountableId || ""}
                            onChange={handleBusinessAccountableChange}
                            users={usersResponse || []}
                            loading={loadingUsers}
                            placeholder="Enter email or select user"
                            className="mt-1"
                          />
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.businessAccountable?.name ||
                              "Not assigned"}
                          </p>
                        )}
                      </div>

                      {/* SDL Responsible */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={
                            productFieldDescriptions.sdlResponsibleId
                          }
                        >
                          SDL Responsible
                        </FieldLabel>
                        {isEditing ? (
                          <TypeaheadUserInput
                            id="sdlResponsibleId"
                            name="sdlResponsibleId"
                            value={editForm.sdlResponsibleId || ""}
                            onChange={handleSdlResponsibleChange}
                            users={usersResponse || []}
                            loading={loadingUsers}
                            placeholder="Enter email or select user"
                            className="mt-1"
                          />
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.sdlResponsible?.name || "Not assigned"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Classification Section — always available so the CRA class can be
                set or updated from product information, even after a CRA
                Applicability Assessment result already exists. */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Classification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CRA Classification */}
                      <div>
                        <FieldLabel
                          className="block text-sm font-medium text-gray-500 dark:text-gray-400"
                          description={
                            productFieldDescriptions.craClassification
                          }
                        >
                          CRA Classification
                        </FieldLabel>
                        {isEditing ? (
                          <select
                            value={editForm.craClassification}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                craClassification: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            <option value="">Select CRA classification</option>
                            {CRA_CLASSIFICATION_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                            {product.craClassification || "Not classified"}
                          </p>
                        )}
                        {product.craApplicability &&
                          product.craApplicability !== "not_assessed" && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              A CRA Applicability Assessment result exists for
                              this product. Setting a CRA Classification here is
                              used by the CRA Compliance assessment.
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Assessment Status (CRA + SDL) */}
            {SHOW_SDL_FEATURES && (
              <SDLAssessmentPanel
                productId={id!}
                productName={product.name}
                productOwnerId={product.ownerId}
                userId={user?.id}
                user={user}
                sdlMaturityScore={productResponse.sdlMaturityScore}
                sdlPracticeScores={productResponse.sdlPracticeScores}
                sdlAssessmentDate={
                  assessmentStatusData?.data?.data?.sdlAssessmentDate
                }
                assessmentId={assessmentStatusData?.data?.data?.assessmentId}
                periods={periods}
                periodsLoading={periodsLoading}
                craApplicability={product.craApplicability}
                craApplicabilityAudit={
                  assessmentStatusData?.data?.data?.craApplicabilityAudit
                }
                craComplianceAudit={
                  assessmentStatusData?.data?.data?.craComplianceAudit
                }
                sdlAudit={assessmentStatusData?.data?.data?.sdlAudit}
                dataActSlot={
                  SHOW_DATAACT_FEATURES ? (
                    <DataActAssessmentPanel
                      productId={id!}
                      canManageAssessment={canManageAssessment(
                        user,
                        product.ownerId,
                      )}
                      audit={assessmentStatusData?.data?.data?.dataActAudit}
                    />
                  ) : undefined
                }
              />
            )}

            {/* When SDL features are hidden there is no "Assessment Status" card
              to host the Data Act panel, so render it standalone. */}
            {!SHOW_SDL_FEATURES && SHOW_DATAACT_FEATURES && (
              <DataActAssessmentPanel
                productId={id!}
                canManageAssessment={canManageAssessment(user, product.ownerId)}
                audit={assessmentStatusData?.data?.data?.dataActAudit}
              />
            )}

            {/* Recent Changes */}
            {product.changes && product.changes.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Recent Changes
                </h2>
                <div className="space-y-3">
                  {product.changes.slice(0, 5).map((change: any) => (
                    <div
                      key={change.id}
                      className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          <span className="font-medium">
                            {change.user.name}
                          </span>{" "}
                          changed{" "}
                          <span className="font-medium">{change.field}</span>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(change.changedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Product Metadata */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Product Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        product.status === "Approved"
                          ? "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                      }`}
                    >
                      {product.status === "Approved" ? (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      ) : (
                        <Clock className="h-3 w-3 mr-1" />
                      )}
                      {product.status}
                    </span>
                    {product.lifecycleStatus === "Retired" && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Retired
                      </span>
                    )}
                    {product.lifecycleStatus === "Deleted" && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200">
                        <Trash2 className="h-3 w-3 mr-1" />
                        Deleted
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Created
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {new Date(product.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Updated
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {new Date(product.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Changed By
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {product.lastChangedByUser?.name ||
                      product.lastChangedBy ||
                      "Unknown"}
                  </p>
                </div>
              </div>
            </div>

            {/* Ownership Requests */}
            {product.ownershipRequests &&
              product.ownershipRequests.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Pending Ownership Requests
                  </h3>
                  <div className="space-y-3">
                    {product.ownershipRequests.map((request: any) => (
                      <div
                        key={request.id}
                        className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          <span className="font-medium">
                            {request.requestedBy.name}
                          </span>{" "}
                          requested ownership
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(request.requestedAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-2">
                {/* Approve button for Division Admins and BA Admins */}
                {((user?.roleId === "division_admin" &&
                  user?.businessArea === product.businessArea &&
                  user?.division === product.division) ||
                  (user?.roleId === "ba_admin" &&
                    user?.businessArea === product.businessArea)) &&
                  product.status !== "Approved" && (
                    <button
                      onClick={handleApprove}
                      disabled={approveMutation.isLoading}
                      className="btn btn-primary w-full text-sm"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {approveMutation.isLoading
                        ? "Approving..."
                        : "Approve Product"}
                    </button>
                  )}

                {/* Retire button */}
                {(isAdmin(user) || product.ownerId === user?.id) &&
                  product.lifecycleStatus !== "Retired" && (
                    <button
                      onClick={handleRetire}
                      disabled={retireMutation.isLoading}
                      className="btn btn-outline w-full text-sm text-amber-600 border-amber-600 hover:bg-amber-50"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      {retireMutation.isLoading
                        ? "Retiring..."
                        : "Retire Product"}
                    </button>
                  )}

                {/* Un-retire button for admins on retired/deleted products */}
                {isAdmin(user) &&
                  (product.lifecycleStatus === "Retired" ||
                    product.lifecycleStatus === "Deleted") && (
                    <button
                      onClick={handleUnretire}
                      disabled={unretireMutation.isLoading}
                      className="btn btn-outline w-full text-sm text-green-600 border-green-600 hover:bg-green-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {unretireMutation.isLoading
                        ? "Un-retiring..."
                        : "Un-retire Product"}
                    </button>
                  )}

                {/* Permanently delete button for admins on retired/deleted products */}
                {isAdmin(user) &&
                  (product.lifecycleStatus === "Retired" ||
                    product.lifecycleStatus === "Deleted") && (
                    <button
                      onClick={handlePermanentDelete}
                      disabled={permanentDeleteMutation.isLoading}
                      className="btn btn-outline w-full text-sm text-red-600 border-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {permanentDeleteMutation.isLoading
                        ? "Deleting..."
                        : "Permanently Delete Product"}
                    </button>
                  )}

                {SHOW_REQUEST_OWNERSHIP && product.ownerId !== user?.id && (
                  <button className="btn btn-outline w-full text-sm">
                    Request Ownership
                  </button>
                )}
              </div>
            </div>

            {/* Applications */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Layers className="h-5 w-5" /> Applications
                  <span className="text-sm font-normal text-gray-500">
                    ({product.applications?.length || 0})
                  </span>
                </h3>
                {(isAdmin(user) || product.ownerId === user?.id) && (
                  <button
                    onClick={() => setShowLinkApplications(true)}
                    className="btn btn-primary text-sm flex items-center gap-1"
                  >
                    <Plus className="h-3.5 w-3.5" /> Link
                  </button>
                )}
              </div>
              {showLinkApplications && (
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search applications..."
                        value={applicationSearch}
                        onChange={(e) => setApplicationSearch(e.target.value)}
                        className="input pl-10 w-full text-sm"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setShowLinkApplications(false);
                        setApplicationSearch("");
                      }}
                      className="btn btn-secondary text-sm"
                    >
                      Close
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {availableApplications.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">
                        No applications found.
                      </p>
                    ) : (
                      availableApplications.map((a) => (
                        <button
                          key={a.id}
                          onClick={() =>
                            linkApplicationsMutation.mutate([a.id])
                          }
                          className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center"
                        >
                          <span className="text-gray-900 dark:text-gray-100">
                            {a.name}
                          </span>
                          <Plus className="h-3.5 w-3.5 text-gray-400" />
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {!product.applications?.length ? (
                  <p className="px-6 py-8 text-center text-sm text-gray-500">
                    No applications linked yet.
                  </p>
                ) : (
                  product.applications.map((a: Application) => (
                    <div
                      key={a.id}
                      className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <Link
                        to={`/applications/${a.id}`}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {a.name}
                      </Link>
                      {(isAdmin(user) || product.ownerId === user?.id) && (
                        <button
                          onClick={() => unlinkApplicationMutation.mutate(a.id)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Unlink application"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
